import React, { useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Pin,
  PinOff,
  LogOut,
  ChevronRight,
  User,
  Building,
  PlusCircle,
  Check,
  Edit2,
  Trash2,
  MoreVertical,
} from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import { useSidebarStore } from '@/stores/useSidebarStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useBillingStore } from '@/stores/useBillingStore';
import { useWorkspaceStore, type WorkspaceItem } from '@/stores/useWorkspaceStore';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from './ThemeToggle';
import { SidebarUsageMeter } from './SidebarUsageMeter';
import { navItems } from './navConfig';
import { useUnreadSummary } from '@/hooks/useUnreadSummary';
import { toast } from 'sonner';
import { CreateWorkspaceModal } from '@/features/workspaces/components/CreateWorkspaceModal';
import { RenameWorkspaceModal } from '@/features/workspaces/components/RenameWorkspaceModal';
import { DeleteWorkspaceModal } from '@/features/workspaces/components/DeleteWorkspaceModal';

export const Sidebar: React.FC = () => {
  const { isLocked, isHovered, toggleLock, setHovered } = useSidebarStore();
  const { user, logout } = useAuthStore();
  const { workspaces, activeWorkspaceId, currentRole, setWorkspace } = useWorkspaceStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { formattedBadge } = useUnreadSummary();
  const billingStatus = useBillingStore((state) => state.status);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [targetWorkspace, setTargetWorkspace] = useState<WorkspaceItem | null>(null);

  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isExpanded =
    isLocked ||
    isHovered ||
    isProfileOpen ||
    isThemeOpen ||
    isWorkspaceOpen ||
    isCreateModalOpen ||
    isRenameModalOpen ||
    isDeleteModalOpen;
  const hasCustomerAccess = billingStatus ? Boolean(billingStatus.allowedFeatures?.includes('CustomersPageAccess')) : true;

  const handleCreateWorkspaceClick = () => {
    setIsWorkspaceOpen(false); // Close dropdown
    setIsCreateModalOpen(true); // Open modal
  };

  const handleRenameWorkspaceClick = (ws: WorkspaceItem, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setTargetWorkspace(ws);
    setIsWorkspaceOpen(false);
    setIsRenameModalOpen(true);
  };

  const handleDeleteWorkspaceClick = (ws: WorkspaceItem, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setTargetWorkspace(ws);
    setIsWorkspaceOpen(false);
    setIsDeleteModalOpen(true);
  };

  const handleMouseEnter = () => {
    if (isLocked) return;
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setHovered(true);
    }, 150);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setHovered(false);
    }, 150);
  };

  const handleLogout = () => {
    logout();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    toast.info('Você saiu da sua conta.');
    navigate('/login', { replace: true });
  };

  const userName = user?.fullName || 'Diego Fernandes';
  const userEmail = user?.email || 'admin@taleshq.com';
  const initials = getInitials(userName, userEmail);
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);

  return (
    <aside
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        'relative flex flex-col h-full bg-card border-r border-border select-none transition-all duration-200 ease-in-out z-30',
        isExpanded ? 'w-64 shadow-xl lg:shadow-none' : 'w-16'
      )}
    >
      {/* Top Header / Workspace Switcher */}
      <div className="h-16 flex items-center justify-between px-3.5 border-b border-border/70 flex-shrink-0">
        <DropdownMenu onOpenChange={setIsWorkspaceOpen}>
          <DropdownMenuTrigger asChild>
            <div
              className={cn(
                'flex items-center gap-3 cursor-pointer overflow-hidden transition-all flex-1 hover:bg-muted/50 rounded p-1',
                !isExpanded && 'justify-center'
              )}
            >
              <div className="h-9 w-9 flex items-center justify-center flex-shrink-0 bg-primary/10 rounded-md">
                <Building className="h-5 w-5 text-primary" />
              </div>

              {isExpanded && (
                <div className="flex flex-col min-w-0 transition-opacity duration-200 flex-1">
                  <span className="font-bold text-sm tracking-tight truncate">
                    {activeWorkspace?.name || 'Selecione um Workspace'}
                  </span>
                  <span className="text-[10px] text-muted-foreground truncate uppercase font-medium">
                    {currentRole || 'Nenhum'}
                  </span>
                </div>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start">
            <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {workspaces.map((ws) => {
              const isActive = ws.id === activeWorkspaceId;
              const canManage = ws.role === 'Owner' || ws.role === 'Admin';
              const canDelete = ws.role === 'Owner';

              return (
                <div
                  key={ws.id}
                  onClick={() => setWorkspace(ws.id)}
                  className={cn(
                    'group/item relative flex items-center justify-between px-2 py-1.5 rounded-sm text-sm cursor-pointer select-none outline-none transition-colors hover:bg-accent hover:text-accent-foreground',
                    isActive && 'bg-accent/50 font-medium text-accent-foreground'
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0 pr-2">
                    <Building className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{ws.name}</span>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {/* Action Menu (Option 2: 3-dots hover action per workspace) */}
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className={cn(
                              'h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background/80 transition-opacity',
                              'opacity-0 group-hover/item:opacity-100 focus:opacity-100 data-[state=open]:opacity-100'
                            )}
                            title="Opções do workspace"
                          >
                            <MoreVertical className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          side="right"
                          align="start"
                          className="w-40 z-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenuItem
                            onClick={(e) => handleRenameWorkspaceClick(ws, e)}
                            className="cursor-pointer gap-2 text-xs"
                          >
                            <Edit2 className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>Renomear</span>
                          </DropdownMenuItem>

                          {canDelete && (
                            <DropdownMenuItem
                              onClick={(e) => handleDeleteWorkspaceClick(ws, e)}
                              className="cursor-pointer gap-2 text-xs text-destructive focus:text-destructive focus:bg-destructive/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              <span>Excluir</span>
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}

                    {isActive && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </div>
                </div>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleCreateWorkspaceClick} className="cursor-pointer gap-2">
              <PlusCircle className="h-4 w-4 text-primary" />
              <span className="text-primary font-medium">Criar novo workspace</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Lock Toggle Button */}
        {isExpanded && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleLock}
                className={cn(
                  'h-8 w-8 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 ml-2',
                  isLocked && 'bg-accent text-accent-foreground'
                )}
                aria-label={isLocked ? 'Desbloquear menu' : 'Fixar menu'}
              >
                {isLocked ? (
                  <Pin className="h-4 w-4 fill-current text-primary" />
                ) : (
                  <PinOff className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{isLocked ? 'Desbloquear menu lateral' : 'Fixar menu lateral'}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Navigation Items List */}
      <nav className="flex-1 py-4 px-2 space-y-1.5 overflow-y-auto overflow-x-hidden">
        {navItems
          .filter((item) => !item.allowedRoles || (currentRole && item.allowedRoles.includes(currentRole)))
          .map((baseItem) => {
            const item = { ...baseItem };
            if (item.to === '/conversations') {
              item.badge = formattedBadge;
              if (formattedBadge) {
                item.badgeVariant = 'default';
              }
            }
            if (item.to === '/customers' && !hasCustomerAccess) {
              item.badge = 'Pro';
              item.badgeVariant = 'secondary';
            }
            const Icon = item.icon;
            const isActive =
              item.to === '/conversations'
                ? location.pathname.startsWith('/conversations')
                : location.pathname === item.to;

            const linkContent = (
              <NavLink
                to={item.to}
                id={`tour-nav-${item.to.replace('/', '')}`}
                className={cn(
                  'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors relative',
                  isActive
                    ? 'bg-accent text-accent-foreground font-semibold shadow-xs'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  !isExpanded && 'justify-center px-0'
                )}
              >
                {isActive && (
                  <div className="absolute left-0 top-1.5 bottom-1.5 w-1 bg-primary rounded-r" />
                )}

                <Icon
                  className={cn(
                    'h-5 w-5 flex-shrink-0 transition-colors',
                    isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                  )}
                />

                {isExpanded && (
                  <div className="flex flex-1 items-center justify-between min-w-0">
                    <span className="truncate">{item.label}</span>
                    {item.badge && (
                      <Badge
                        variant={item.badgeVariant || 'default'}
                        className={cn(
                          'text-[10px] px-1.5 py-0 h-4 font-bold flex-shrink-0',
                          item.badgeVariant === 'outline' && 'text-muted-foreground'
                        )}
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </div>
                )}
              </NavLink>
            );

            if (!isExpanded) {
              return (
                <Tooltip key={item.to}>
                  <TooltipTrigger asChild>
                    <div>{linkContent}</div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="flex items-center gap-2">
                    <span>{item.label}</span>
                    {item.badge && (
                      <Badge variant={item.badgeVariant || 'default'} className="text-[10px] px-1 py-0 h-3.5">
                        {item.badge}
                      </Badge>
                    )}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return <div key={item.to}>{linkContent}</div>;
          })}
      </nav>

      {/* Bottom Utility Controls */}
      <div className="p-2 border-t border-border/70 space-y-2 flex-shrink-0">
        {currentRole === 'Owner' && <SidebarUsageMeter isExpanded={isExpanded} />}

        {/* Theme Switcher Bar */}
        {isExpanded ? (
          <div className="px-2 py-1.5 rounded-lg bg-muted/50 flex items-center justify-between text-xs text-muted-foreground">
            <span className="font-medium">Tema</span>
            <ThemeToggle onOpenChange={setIsThemeOpen} />
          </div>
        ) : (
          <div className="flex justify-center">
            <ThemeToggle onOpenChange={setIsThemeOpen} />
          </div>
        )}

        {/* User Account Popover */}
        <DropdownMenu onOpenChange={setIsProfileOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                'w-full flex items-center gap-3 rounded-lg p-1.5 hover:bg-muted transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                !isExpanded && 'justify-center'
              )}
            >
              <Avatar className="h-8 w-8 border border-border flex-shrink-0">
                <AvatarFallback className="bg-primary/10 text-primary font-bold text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>

              {isExpanded && (
                <div className="flex flex-1 items-center justify-between min-w-0">
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-semibold text-foreground truncate">
                      {userName}
                    </span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {userEmail}
                    </span>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                </div>
              )}
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent side={isExpanded ? 'top' : 'right'} align="start" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none text-foreground">{userName}</p>
                <p className="text-xs leading-none text-muted-foreground">{userEmail}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer gap-2.5">
              <User className="h-4 w-4 text-muted-foreground" />
              <span>Perfil e Conta</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="cursor-pointer gap-2.5 text-destructive focus:text-destructive focus:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
              <span>Sair</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CreateWorkspaceModal open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen} />
      {(targetWorkspace || activeWorkspace) && (
        <>
          <RenameWorkspaceModal
            open={isRenameModalOpen}
            onOpenChange={setIsRenameModalOpen}
            workspaceId={(targetWorkspace || activeWorkspace)!.id}
            currentName={(targetWorkspace || activeWorkspace)!.name}
          />
          <DeleteWorkspaceModal
            open={isDeleteModalOpen}
            onOpenChange={setIsDeleteModalOpen}
            workspaceId={(targetWorkspace || activeWorkspace)!.id}
            workspaceName={(targetWorkspace || activeWorkspace)!.name}
          />
        </>
      )}
    </aside>
  );
};

